// Canonical crew roles — shared between the producer's saved team
// ("My Team" → team_members.role) and the briefing crew editor
// (CreateEditBriefing → crew_members.function). The two used to keep
// their own identical copies of this list, which risked drifting; this
// module is the single source of truth.
//
// The camera/photo subset (CAMERA_ID_ROLES in ./cameraId) drives CAM ID
// eligibility: a team member's `role` is copied verbatim into a crew
// member's `function`, and CAM ID slots are only offered when
// isCameraIdRole(function) is true. So when we import roles from a
// pasted spreadsheet we must resolve free text back onto these canonical
// values — otherwise a "Cameraman" never gets a CAM ID.

// Values are stored verbatim in team_members.role / crew_members.function
// and rendered with CSS `capitalize` on web, so acronyms (FPV, RT) keep
// their casing here — `capitalize` only touches the first letter of each
// word. Matching stays case-insensitive via resolveCrewRole.
export const CREW_ROLES = [
  "video general",
  "photo",
  "video aftermovie",
  "video recap",
  "video one-taker",
  "video artist snippets",
  "video interviews",
  "video livestream",
  "drone pilot",
  "FPV pilot",
  "cinematic drone pilot",
  "drone spotter",
  "drone RT",
  "drone assist",
  "video spotter",
  "editor",
  "assist editor",
  "spotter",
  "datahandler",
  "assist",
  "producer",
] as const;

export type CrewRole = (typeof CREW_ROLES)[number];

// Normalized (lowercase) → canonical value, so mixed-case canonicals like
// "FPV pilot" still match case-insensitively and resolve back to their
// canonical casing.
const CANONICAL_BY_NORMALIZED: ReadonlyMap<string, CrewRole> = new Map(
  CREW_ROLES.map((role) => [role.toLowerCase(), role]),
);

// Roles a dedicated assistant/runner can be attached to: the filming
// roles (all video + photo + drone flying). It makes no sense to give an
// editor or a data handler an assistant, so they are excluded from the
// assist UI.
export const ASSISTABLE_ROLES = [
  "video general",
  "photo",
  "video aftermovie",
  "video recap",
  "video one-taker",
  "video artist snippets",
  "video interviews",
  "video livestream",
  "drone pilot",
  "FPV pilot",
  "cinematic drone pilot",
] as const satisfies readonly CrewRole[];

const ASSISTABLE_ROLE_SET: ReadonlySet<string> = new Set(ASSISTABLE_ROLES);

// Roles that count as an assistant in the crew planner — these people can
// be linked to a filming crew member as their dedicated assist. The drone
// support roles are here because a pilot flies with a spotter (keeps the
// aircraft in visual line of sight) and an RT operator (handles radio
// contact); both shadow the pilot's schedule, so both need to be linkable —
// and a pilot can carry one of each, since a member takes any number of
// assistants.
export const ASSISTANT_ROLES = [
  "assist",
  "drone assist",
  "drone spotter",
  "drone RT",
  "assist editor",
] as const satisfies readonly CrewRole[];

const ASSISTANT_ROLE_SET: ReadonlySet<string> = new Set(ASSISTANT_ROLES);

/**
 * True when a (free-text) crew function resolves to a filming role that a
 * dedicated assistant can be attached to. Normalises via resolveCrewRole so
 * spreadsheet spellings like "Cameraman" also match.
 */
export function isAssistableRole(fn: string | null | undefined): boolean {
  return ASSISTABLE_ROLE_SET.has(resolveCrewRole(fn).role);
}

/** True when a crew function resolves to one of the ASSISTANT_ROLES above. */
export function isAssistantRole(fn: string | null | undefined): boolean {
  return ASSISTANT_ROLE_SET.has(resolveCrewRole(fn).role);
}

// Common free-text spellings producers paste from a spreadsheet, mapped
// onto a canonical role. Keys are already normalized (lowercase). Kept
// deliberately small and high-confidence — camera/photo terms matter
// most because they unlock CAM ID assignment. Anything not covered here
// falls through to "unmatched" and is preserved as free text.
const ROLE_SYNONYMS: Readonly<Record<string, CrewRole>> = {
  camera: "video general",
  cameraman: "video general",
  cameraperson: "video general",
  "camera operator": "video general",
  cam: "video general",
  dop: "video general",
  "director of photography": "video general",
  video: "video general",
  videographer: "video general",
  photo: "photo",
  photographer: "photo",
  fotograaf: "photo",
  foto: "photo",
  fotografie: "photo",
  photography: "photo",
  aftermovie: "video aftermovie",
  recap: "video recap",
  "one-taker": "video one-taker",
  onetaker: "video one-taker",
  "one taker": "video one-taker",
  interviews: "video interviews",
  interview: "video interviews",
  livestream: "video livestream",
  "live stream": "video livestream",
  edit: "editor",
  editing: "editor",
  assistant: "assist",
  data: "datahandler",
  "data handler": "datahandler",
  drone: "drone pilot",
  "drone operator": "drone pilot",
  dronepiloot: "drone pilot",
  "drone piloot": "drone pilot",
  fpv: "FPV pilot",
  "fpv drone": "FPV pilot",
  "fpv piloot": "FPV pilot",
  "cinematic drone": "cinematic drone pilot",
  "drone radiotelefonie": "drone RT",
  "assistant editor": "assist editor",
};

const normalizeRole = (input: string | null | undefined): string =>
  (input ?? "").trim().replace(/\s+/g, " ").toLowerCase();

export interface ResolvedRole {
  /** Canonical role when matched, otherwise the trimmed input verbatim. */
  role: string;
  /** True when the input resolved onto a canonical CREW_ROLES value. */
  matched: boolean;
}

/**
 * Resolve free-text role input (e.g. from a pasted spreadsheet cell)
 * onto a canonical CREW_ROLES value. Matching is case-insensitive and
 * whitespace-tolerant, and consults a small synonym table so common
 * spellings of camera/photo functions land on the right canonical role
 * (which is what makes them CAM ID-eligible downstream).
 *
 * No match → { role: <trimmed input>, matched: false } so the producer
 * can still store a custom function, just without canonical guarantees.
 */
export function resolveCrewRole(input: string | null | undefined): ResolvedRole {
  const normalized = normalizeRole(input);
  if (!normalized) return { role: "", matched: false };
  const canonical = CANONICAL_BY_NORMALIZED.get(normalized);
  if (canonical) return { role: canonical, matched: true };
  const synonym = ROLE_SYNONYMS[normalized];
  if (synonym) return { role: synonym, matched: true };
  return { role: (input ?? "").trim(), matched: false };
}
